import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Calendar,
  Stethoscope,
  Plus,
  Search,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  TrendingUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Animal, HealthRecord } from '../LivestockManagementPage';

// Import design system tokens
import { 
  getCardClasses, 
  getShadowClasses, 
  getButtonClasses,
  components, 
  colors, 
  typography,
  spacing
} from '@/components/business/design-system/businessDesignTokens';

interface HealthTabProps {
  animals: Animal[];
  healthRecords: HealthRecord[];
  setHealthRecords: React.Dispatch<React.SetStateAction<HealthRecord[]>>;
}

export function HealthTab({ animals, healthRecords, setHealthRecords }: HealthTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock.healthTab' });
  const { t: tc } = useTranslation('common');
  const [searchTerm, setSearchTerm] = useState('');
  const [animalFilter, setAnimalFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<HealthRecord | null>(null);
  
  // Form state for adding health records
  const [formData, setFormData] = useState<Partial<HealthRecord>>({
    animalId: '',
    date: new Date().toISOString().split('T')[0],
    type: 'Checkup',
    veterinarian: '',
    symptoms: '',
    diagnosis: '',
    treatment: '',
    medication: [],
    nextDue: '',
    notes: '',
    weight: 0,
    bodyConditionScore: 5
  });

  // Filter health records
  const filteredRecords = healthRecords.filter(record => {
    const animal = animals.find(a => a.id === record.animalId);
    const matchesSearch = searchTerm === '' || 
      animal?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.veterinarian.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAnimal = animalFilter === 'all' || record.animalId === animalFilter;
    const matchesType = typeFilter === 'all' || record.type === typeFilter;
    
    return matchesSearch && matchesAnimal && matchesType;
  });

  // Get upcoming health events
  const upcomingEvents = healthRecords.filter(record => {
    if (!record.nextDue) return false;
    const dueDate = new Date(record.nextDue);
    const today = new Date();
    const weekFromToday = new Date();
    weekFromToday.setDate(today.getDate() + 7);
    return dueDate >= today && dueDate <= weekFromToday;
  });

  // Health statistics
  const healthStats = {
    totalRecords: healthRecords.length,
    upcomingEvents: upcomingEvents.length,
    animalsUnderTreatment: animals.filter(a => a.status === 'Under Treatment').length,
    criticalAnimals: animals.filter(a => a.status === 'Critical').length
  };

  const handleFormChange = (field: string, value: any) => {
    if (field === 'medication') {
      setFormData(prev => ({ ...prev, medication: value }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addMedication = () => {
    const newMed = { name: '', dosage: '', frequency: '', duration: '' };
    setFormData(prev => ({
      ...prev,
      medication: [...(prev.medication || []), newMed]
    }));
  };

  const updateMedication = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      medication: prev.medication?.map((med, i) => 
        i === index ? { ...med, [field]: value } : med
      )
    }));
  };

  const removeMedication = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medication: prev.medication?.filter((_, i) => i !== index)
    }));
  };

  const handleSaveRecord = () => {
    if (!formData.animalId || !formData.veterinarian) {
      alert('Please fill in required fields');
      return;
    }

    const newRecord: HealthRecord = {
      ...formData,
      id: `h${Date.now()}`,
      medication: formData.medication || []
    } as HealthRecord;
    
    setHealthRecords(prev => [...prev, newRecord]);

    // Reset form
    setFormData({
      animalId: '',
      date: new Date().toISOString().split('T')[0],
      type: 'Checkup',
      veterinarian: '',
      symptoms: '',
      diagnosis: '',
      treatment: '',
      medication: [],
      nextDue: '',
      notes: '',
      weight: 0,
      bodyConditionScore: 5
    });
    setShowAddDialog(false);
  };

  const getAnimalName = (animalId: string) => {
    const animal = animals.find(a => a.id === animalId);
    return animal ? animal.name : 'Unknown Animal';
  };

  const getTypeColor = (type: HealthRecord['type']) => {
    switch (type) {
      case 'Consultation': return 'default';
      case 'Treatment': return 'secondary';
      case 'Vaccination': return 'outline';
      case 'Checkup': return 'default';
      case 'Lab Result': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">{t('description')}</p>
      </div>

      {/* Streamlined Stats and Actions */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-4">
          {/* Compact Stats Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900">{healthStats.totalRecords}</div>
                  <div className="text-xs text-slate-600">Total Records</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">{healthStats.upcomingEvents}</div>
                  <div className="text-xs text-slate-600">Upcoming</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-600">{healthStats.animalsUnderTreatment}</div>
                  <div className="text-xs text-slate-600">Under Treatment</div>
                </div>
                {healthStats.criticalAnimals > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">{healthStats.criticalAnimals}</div>
                    <div className="text-xs text-slate-600">Critical</div>
                  </div>
                )}
              </div>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Health Record
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compact Search and Filters */}
      <div className="space-y-4">
        {/* Compact Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by animal, diagnosis, or veterinarian..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={animalFilter} onValueChange={setAnimalFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Animal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Animals</SelectItem>
                  {animals.map(animal => (
                    <SelectItem key={animal.id} value={animal.id}>
                      {animal.name} ({animal.species})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Consultation">Consultation</SelectItem>
                  <SelectItem value="Treatment">Treatment</SelectItem>
                  <SelectItem value="Vaccination">Vaccination</SelectItem>
                  <SelectItem value="Checkup">Checkup</SelectItem>
                  <SelectItem value="Lab Result">Lab Result</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center justify-between text-sm py-2 border-t border-slate-100">
          <span className="text-slate-600">
            Showing {filteredRecords.length} of {healthRecords.length} health records
          </span>
          {(searchTerm || animalFilter !== 'all' || typeFilter !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setAnimalFilter('all');
                setTypeFilter('all');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Upcoming Events Alert */}
      {upcomingEvents.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-900">
              <Clock className="w-5 h-5" />
              <span>Upcoming Health Events</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {upcomingEvents.slice(0, 3).map(event => (
                <div key={event.id} className="flex justify-between items-center p-2 bg-white rounded">
                  <div>
                    <span className="font-medium">{getAnimalName(event.animalId)}</span>
                    <span className="text-sm text-slate-600 ml-2">{event.type}</span>
                  </div>
                  <div className="text-sm text-blue-700">
                    {new Date(event.nextDue!).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {upcomingEvents.length > 3 && (
                <p className="text-sm text-blue-700">
                  And {upcomingEvents.length - 3} more events this week
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health Records List */}
      <Card className={getCardClasses('basic')}>
        <CardHeader>
          <CardTitle>Medical Records</CardTitle>
          <CardDescription>
            Showing {filteredRecords.length} of {healthRecords.length} records
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-8">
              <Stethoscope className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No health records found</h3>
              <p className="text-slate-600 mb-4">
                {healthRecords.length === 0 
                  ? "Start by adding your first health record"
                  : "Try adjusting your search criteria"
                }
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add First Record
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRecords
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map(record => (
                  <div 
                    key={record.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedRecord(record);
                      setShowViewDialog(true);
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-3">
                        <Badge variant={getTypeColor(record.type)}>
                          {record.type}
                        </Badge>
                        <h3 className="font-medium text-slate-900">
                          {getAnimalName(record.animalId)}
                        </h3>
                      </div>
                      <div className="text-sm text-slate-500">
                        {new Date(record.date).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="text-sm text-slate-600 mb-2">
                      <span className="font-medium">Veterinarian:</span> {record.veterinarian}
                    </div>
                    
                    {record.diagnosis && (
                      <div className="text-sm text-slate-900 mb-2">
                        <span className="font-medium">Diagnosis:</span> {record.diagnosis}
                      </div>
                    )}
                    
                    {record.treatment && (
                      <div className="text-sm text-slate-700 mb-2">
                        <span className="font-medium">Treatment:</span> {record.treatment}
                      </div>
                    )}
                    
                    {record.nextDue && (
                      <div className="text-sm text-blue-600 flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>Follow-up due: {new Date(record.nextDue).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Health Record Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Health Record</DialogTitle>
            <DialogDescription>
              Record a medical event, treatment, or checkup
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="animalId">Animal *</Label>
                <Select value={formData.animalId} onValueChange={(value) => handleFormChange('animalId', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select animal" />
                  </SelectTrigger>
                  <SelectContent>
                    {animals.map(animal => (
                      <SelectItem key={animal.id} value={animal.id}>
                        {animal.name} ({animal.species})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => handleFormChange('date', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(value) => handleFormChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Consultation">Consultation</SelectItem>
                    <SelectItem value="Treatment">Treatment</SelectItem>
                    <SelectItem value="Vaccination">Vaccination</SelectItem>
                    <SelectItem value="Checkup">Checkup</SelectItem>
                    <SelectItem value="Lab Result">Lab Result</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="veterinarian">Veterinarian *</Label>
                <Input
                  id="veterinarian"
                  value={formData.veterinarian || ''}
                  onChange={(e) => handleFormChange('veterinarian', e.target.value)}
                  placeholder="Dr. name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="symptoms">Symptoms</Label>
              <Textarea
                id="symptoms"
                value={formData.symptoms || ''}
                onChange={(e) => handleFormChange('symptoms', e.target.value)}
                placeholder="Observed symptoms..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="diagnosis">Diagnosis</Label>
              <Textarea
                id="diagnosis"
                value={formData.diagnosis || ''}
                onChange={(e) => handleFormChange('diagnosis', e.target.value)}
                placeholder="Medical diagnosis..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="treatment">Treatment</Label>
              <Textarea
                id="treatment"
                value={formData.treatment || ''}
                onChange={(e) => handleFormChange('treatment', e.target.value)}
                placeholder="Treatment provided..."
                rows={2}
              />
            </div>

            {/* Medications */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Medications</Label>
                <Button type="button" variant="outline" size="sm" onClick={addMedication}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Medication
                </Button>
              </div>
              
              {formData.medication?.map((med, index) => (
                <div key={index} className="grid grid-cols-4 gap-2 mb-2">
                  <Input
                    placeholder="Medication name"
                    value={med.name}
                    onChange={(e) => updateMedication(index, 'name', e.target.value)}
                  />
                  <Input
                    placeholder="Dosage"
                    value={med.dosage}
                    onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                  />
                  <Input
                    placeholder="Frequency"
                    value={med.frequency}
                    onChange={(e) => updateMedication(index, 'frequency', e.target.value)}
                  />
                  <div className="flex space-x-1">
                    <Input
                      placeholder="Duration"
                      value={med.duration}
                      onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => removeMedication(index)}
                    >
                      ×
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  value={formData.weight || ''}
                  onChange={(e) => handleFormChange('weight', parseFloat(e.target.value) || 0)}
                  placeholder="Weight measurement"
                />
              </div>
              
              <div>
                <Label htmlFor="nextDue">Follow-up Due Date</Label>
                <Input
                  id="nextDue"
                  type="date"
                  value={formData.nextDue || ''}
                  onChange={(e) => handleFormChange('nextDue', e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRecord} className="bg-emerald-600 hover:bg-emerald-700">
              Save Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Record Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Stethoscope className="w-5 h-5 text-emerald-600" />
              <span>Health Record Details</span>
            </DialogTitle>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Basic Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">Animal:</span> {getAnimalName(selectedRecord.animalId)}</div>
                    <div><span className="text-slate-600">Date:</span> {new Date(selectedRecord.date).toLocaleDateString()}</div>
                    <div><span className="text-slate-600">Type:</span> {selectedRecord.type}</div>
                    <div><span className="text-slate-600">Veterinarian:</span> {selectedRecord.veterinarian}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Measurements</h3>
                  <div className="space-y-2 text-sm">
                    {selectedRecord.weight && (
                      <div><span className="text-slate-600">Weight:</span> {selectedRecord.weight} kg</div>
                    )}
                    {selectedRecord.bodyConditionScore && (
                      <div><span className="text-slate-600">Body Condition:</span> {selectedRecord.bodyConditionScore}/10</div>
                    )}
                    {selectedRecord.nextDue && (
                      <div><span className="text-slate-600">Follow-up Due:</span> {new Date(selectedRecord.nextDue).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              </div>

              {selectedRecord.symptoms && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Symptoms</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedRecord.symptoms}
                  </p>
                </div>
              )}

              {selectedRecord.diagnosis && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Diagnosis</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedRecord.diagnosis}
                  </p>
                </div>
              )}

              {selectedRecord.treatment && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Treatment</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedRecord.treatment}
                  </p>
                </div>
              )}

              {selectedRecord.medication && selectedRecord.medication.length > 0 && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Medications</h3>
                  <div className="space-y-2">
                    {selectedRecord.medication.map((med, index) => (
                      <div key={index} className="p-2 bg-slate-50 rounded text-sm">
                        <div className="font-medium">{med.name}</div>
                        <div className="text-slate-600">
                          {med.dosage} • {med.frequency} • {med.duration}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.notes && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Notes</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedRecord.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 